import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CreditCard, X, CheckCircle, Loader, Building, User, Mail, Calendar, Lock } from 'lucide-react';

const paymentSchema = z.object({
  cardName: z.string().min(2, 'Name muss mindestens 2 Zeichen lang sein'),
  cardNumber: z.string()
    .min(16, 'Kartennummer muss mindestens 16 Zeichen lang sein')
    .max(19, 'Kartennummer darf maximal 19 Zeichen lang sein')
    .regex(/^[0-9\s-]+$/, 'Kartennummer darf nur Zahlen, Leerzeichen und Bindestriche enthalten'),
  expiryDate: z.string()
    .regex(/^(0[1-9]|1[0-2])\/([0-9]{2})$/, 'Gültig bis muss im Format MM/JJ sein'),
  cvv: z.string()
    .min(3, 'CVV muss mindestens 3 Zeichen lang sein')
    .max(4, 'CVV darf maximal 4 Zeichen lang sein')
    .regex(/^[0-9]+$/, 'CVV darf nur Zahlen enthalten'),
  company: z.string().min(2, 'Firmenname muss mindestens 2 Zeichen lang sein'),
  email: z.string().email('Ungültige E-Mail-Adresse'),
  billingAddress: z.string().min(5, 'Adresse muss mindestens 5 Zeichen lang sein'),
  agreeToTerms: z.boolean().refine(val => val === true, {
    message: 'Sie müssen den Geschäftsbedingungen zustimmen',
  }),
});

type PaymentFormData = z.infer<typeof paymentSchema>;

interface PaymentFormProps {
  plan: string;
  onComplete: () => void;
  onCancel: () => void;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({ plan, onComplete, onCancel }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { register, handleSubmit, formState: { errors } } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      cardName: '',
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      company: '',
      email: '',
      billingAddress: '',
      agreeToTerms: false,
    }
  });

  const getPlanDetails = () => {
    switch (plan) {
      case 'starter':
        return { name: 'Starter', price: '9,9 €' };
      case 'professional':
        return { name: 'Professional', price: '14,9 €' };
      default:
        return { name: 'Starter', price: '9,9 €' };
    }
  };

  const planDetails = getPlanDetails();

  const onFormSubmit = async (data: PaymentFormData) => {
    setIsProcessing(true);
    
    try {
      // Simulate payment processing
      console.log('Processing payment for plan:', plan, 'with data:', data);
      
      // Wait for 2 seconds to simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSuccess(true);
      
      // Wait for 2 seconds to show success message
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error) {
      console.error('Error processing payment:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCardNumber = (value: string) => {
    return value
      .replace(/\s/g, '')
      .replace(/(\d{4})/g, '$1 ')
      .trim();
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-blue-600" />
          <span>Zahlungsinformationen - {planDetails.name} Plan</span>
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {success ? (
        <div className="bg-green-50 p-6 rounded-lg text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-green-800 mb-2">Zahlung erfolgreich!</h3>
          <p className="text-green-700">
            Vielen Dank für Ihren Kauf. Ihr {planDetails.name} Plan ist jetzt aktiv.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-blue-800">{planDetails.name} Plan</h3>
                <p className="text-blue-700 text-sm">Monatliche Abrechnung</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-blue-800">{planDetails.price}</p>
                <p className="text-blue-700 text-xs">zzgl. MwSt.</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Unternehmensinformationen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firmenname <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('company')}
                    type="text"
                    className={`pl-10 block w-full rounded-md border ${errors.company ? 'border-red-300' : 'border-gray-300'} shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    placeholder="Firmenname"
                  />
                </div>
                {errors.company && (
                  <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('email')}
                    type="email"
                    className={`pl-10 block w-full rounded-md border ${errors.email ? 'border-red-300' : 'border-gray-300'} shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    placeholder="rechnungen@ihrefirma.de"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rechnungsadresse <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('billingAddress')}
                rows={3}
                className={`block w-full rounded-md border ${errors.billingAddress ? 'border-red-300' : 'border-gray-300'} shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                placeholder="Straße, Hausnummer, PLZ, Ort"
              />
              {errors.billingAddress && (
                <p className="mt-1 text-sm text-red-600">{errors.billingAddress.message}</p>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Zahlungsinformationen</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name auf der Karte <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('cardName')}
                    type="text"
                    className={`pl-10 block w-full rounded-md border ${errors.cardName ? 'border-red-300' : 'border-gray-300'} shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    placeholder="Max Mustermann"
                  />
                </div>
                {errors.cardName && (
                  <p className="mt-1 text-sm text-red-600">{errors.cardName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kartennummer <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('cardNumber')}
                    type="text"
                    className={`pl-10 block w-full rounded-md border ${errors.cardNumber ? 'border-red-300' : 'border-gray-300'} shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    placeholder="1234 5678 9012 3456"
                    onChange={(e) => {
                      e.target.value = formatCardNumber(e.target.value);
                    }}
                    maxLength={19}
                  />
                </div>
                {errors.cardNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.cardNumber.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gültig bis <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('expiryDate')}
                      type="text"
                      className={`pl-10 block w-full rounded-md border ${errors.expiryDate ? 'border-red-300' : 'border-gray-300'} shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                      placeholder="MM/JJ"
                      maxLength={5}
                    />
                  </div>
                  {errors.expiryDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.expiryDate.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CVV <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('cvv')}
                      type="password"
                      className={`pl-10 block w-full rounded-md border ${errors.cvv ? 'border-red-300' : 'border-gray-300'} shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                      placeholder="123"
                      maxLength={4}
                    />
                  </div>
                  {errors.cvv && (
                    <p className="mt-1 text-sm text-red-600">{errors.cvv.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                {...register('agreeToTerms')}
                id="agreeToTerms"
                type="checkbox"
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="agreeToTerms" className="font-medium text-gray-700">
                Ich stimme den <a href="#" className="text-blue-600 hover:text-blue-500">Geschäftsbedingungen</a> und der <a href="#" className="text-blue-600 hover:text-blue-500">Datenschutzerklärung</a> zu
              </label>
              {errors.agreeToTerms && (
                <p className="mt-1 text-sm text-red-600">{errors.agreeToTerms.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <Loader className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                  Zahlung wird verarbeitet...
                </>
              ) : (
                <>
                  <CreditCard className="-ml-1 mr-2 h-4 w-4" />
                  Jetzt bezahlen ({planDetails.price}/Monat)
                </>
              )}
            </button>
          </div>

          <div className="text-xs text-gray-500 mt-4">
            <p>
              Ihre Zahlungsinformationen werden sicher verarbeitet. Wir speichern keine Kreditkartendaten auf unseren Servern.
              Die Abrechnung erfolgt monatlich und kann jederzeit gekündigt werden.
            </p>
          </div>
        </form>
      )}
    </div>
  );
};